<template>
  <div class="assistant" :class="{ 'assistant--open': open }">
    <transition name="assistant-pop">
      <section v-if="open" class="assistant__panel" role="dialog" aria-label="Help assistant" @keydown.esc="close">
        <header class="assistant__header">
          <div class="assistant__title">
            <span class="assistant__avatar" aria-hidden="true"><i class="fas fa-robot"></i></span>
            <div>
              <strong>Help assistant</strong>
              <small v-if="simulated">Demo mode - simulated replies</small>
              <small v-else>AI assistant - ask how to do things</small>
            </div>
          </div>
          <div class="assistant__actions">
            <button type="button" class="assistant__icon" title="Clear conversation" aria-label="Clear conversation" @click="reset">
              <i class="fas fa-redo"></i>
            </button>
            <button type="button" class="assistant__icon" title="Close" aria-label="Close assistant" @click="close">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </header>

        <div ref="scroller" class="assistant__messages" aria-live="polite">
          <div v-for="message in messages" :key="message.id" class="assistant__row" :class="'assistant__row--' + message.role">
            <div class="assistant__bubble">{{ message.text }}</div>
          </div>
          <div v-if="thinking" class="assistant__row assistant__row--assistant">
            <div class="assistant__bubble assistant__typing" aria-label="The assistant is typing">
              <span></span><span></span><span></span>
            </div>
          </div>
          <div v-if="showSuggestions" class="assistant__suggestions">
            <button v-for="suggestion in suggestions" :key="suggestion" type="button" class="assistant__chip" @click="send(suggestion)">
              {{ suggestion }}
            </button>
          </div>
        </div>

        <form class="assistant__form" @submit.prevent="send()">
          <input
            ref="input"
            v-model="draft"
            type="text"
            class="assistant__input"
            placeholder="Ask how to do something..."
            maxlength="300"
            :disabled="thinking"
            autocomplete="off"
            aria-label="Your question"
          />
          <button type="submit" class="assistant__send" :disabled="!draft.trim() || thinking" aria-label="Send">
            <i class="fas fa-paper-plane"></i>
          </button>
        </form>
      </section>
    </transition>

    <button type="button" class="assistant__fab" :aria-expanded="open" aria-label="Open help assistant" @click="toggle">
      <i class="fas" :class="open ? 'fa-times' : 'fa-comment-dots'"></i>
    </button>
  </div>
</template>

<script>
import { ask, assistantState, checkStatus } from "@/assistant/assistantService";
import { greeting, suggestions } from "@/assistant/knowledge";

const STORAGE_KEY = "assistantConversation";

function load() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (saved && Array.isArray(saved.messages)) return saved;
  } catch (e) {
    // storage unavailable or corrupt: start fresh
  }
  return null;
}

export default {
  name: "AssistantChat",
  data() {
    const saved = load();
    return {
      open: saved ? saved.open : false,
      messages: saved ? saved.messages : [{ id: 1, role: "assistant", text: greeting }],
      draft: "",
      thinking: false,
      suggestions,
      nextId: saved ? saved.messages.length + 1 : 2,
    };
  },
  computed: {
    simulated() {
      return assistantState.simulated;
    },
    showSuggestions() {
      return this.messages.length === 1 && !this.thinking;
    },
  },
  watch: {
    messages: { deep: true, handler() { this.persist(); this.scrollDown(); } },
    open() { this.persist(); },
  },
  mounted() {
    checkStatus();
    if (this.open) this.scrollDown();
  },
  methods: {
    toggle() {
      this.open ? this.close() : this.openPanel();
    },
    openPanel() {
      this.open = true;
      this.$nextTick(() => {
        this.scrollDown();
        if (this.$refs.input) this.$refs.input.focus();
      });
    },
    close() {
      this.open = false;
    },
    reset() {
      this.messages = [{ id: 1, role: "assistant", text: greeting }];
      this.nextId = 2;
      this.draft = "";
    },
    async send(text) {
      const question = (text || this.draft).trim();
      if (!question || this.thinking) return;
      this.messages.push({ id: this.nextId++, role: "user", text: question });
      this.draft = "";
      this.thinking = true;
      this.scrollDown();
      try {
        const reply = await ask(question, this.messages);
        this.messages.push({ id: this.nextId++, role: "assistant", text: reply.text });
      } catch (e) {
        this.messages.push({
          id: this.nextId++,
          role: "assistant",
          text: "Sorry, I could not answer that just now. Please try again.",
        });
      } finally {
        this.thinking = false;
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      }
    },
    persist() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ open: this.open, messages: this.messages }));
      } catch (e) {
        // ignore: the conversation just will not survive a reload
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.scroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.assistant {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2000;
  font-family: "Poppins", sans-serif;
}

.assistant__fab {
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 50%;
  background: #007dfc;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 125, 252, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  transition: transform 0.15s ease, background 0.15s ease;
}

.assistant__fab:hover {
  background: #0069d4;
  transform: scale(1.05);
}

.assistant__fab:focus-visible,
.assistant__icon:focus-visible,
.assistant__chip:focus-visible,
.assistant__send:focus-visible {
  outline: 3px solid #212121;
  outline-offset: 2px;
}

.assistant__panel {
  width: 370px;
  max-width: calc(100vw - 40px);
  height: 520px;
  max-height: calc(100vh - 110px);
  margin-bottom: 14px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.assistant__header {
  background: #212121;
  color: #fff;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.assistant__title {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.2;
}

.assistant__title small {
  display: block;
  opacity: 0.75;
  font-size: 11px;
}

.assistant__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #007dfc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.assistant__actions {
  display: flex;
  gap: 4px;
}

.assistant__icon {
  background: transparent;
  border: 0;
  color: #fff;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  cursor: pointer;
}

.assistant__icon:hover {
  background: rgba(255, 255, 255, 0.15);
}

.assistant__messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  background: #f8fafd;
}

.assistant__row {
  display: flex;
  margin-bottom: 10px;
}

.assistant__row--user {
  justify-content: flex-end;
}

.assistant__bubble {
  max-width: 85%;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 13.5px;
  line-height: 1.45;
  white-space: pre-line;
  word-wrap: break-word;
}

.assistant__row--assistant .assistant__bubble {
  background: #fff;
  color: #212121;
  border: 1px solid #e3e8ef;
  border-bottom-left-radius: 3px;
}

.assistant__row--user .assistant__bubble {
  background: #007dfc;
  color: #fff;
  border-bottom-right-radius: 3px;
}

.assistant__typing {
  display: flex;
  gap: 4px;
  padding: 12px 14px;
}

.assistant__typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #9aa5b5;
  animation: assistant-bounce 1s infinite ease-in-out;
}

.assistant__typing span:nth-child(2) { animation-delay: 0.15s; }
.assistant__typing span:nth-child(3) { animation-delay: 0.3s; }

@keyframes assistant-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
  40% { transform: translateY(-5px); opacity: 1; }
}

.assistant__suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.assistant__chip {
  background: #fff;
  border: 1px solid #007dfc;
  color: #007dfc;
  border-radius: 16px;
  padding: 5px 12px;
  font-size: 12.5px;
  cursor: pointer;
}

.assistant__chip:hover {
  background: #007dfc;
  color: #fff;
}

.assistant__form {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #e3e8ef;
  background: #fff;
}

.assistant__input {
  flex: 1;
  border: 1px solid #cfd6e0;
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 13.5px;
  outline: none;
}

.assistant__input:focus {
  border-color: #007dfc;
}

.assistant__send {
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  background: #007dfc;
  color: #fff;
  cursor: pointer;
}

.assistant__send:disabled {
  background: #a9cffa;
  cursor: not-allowed;
}

.assistant-pop-enter-active,
.assistant-pop-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
  transform-origin: bottom right;
}

.assistant-pop-enter-from,
.assistant-pop-leave-to {
  opacity: 0;
  transform: scale(0.92) translateY(10px);
}

@media (prefers-reduced-motion: reduce) {
  .assistant-pop-enter-active,
  .assistant-pop-leave-active,
  .assistant__fab {
    transition: none;
  }
  .assistant__typing span {
    animation: none;
  }
}
</style>
